import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, FileText, Search } from "lucide-react";
import Slash from "./Slash";
import ScrambleText from "./ScrambleText";
import { ParallaxShader, ParallaxSpan, PointerLayer, TiltCard } from "./Parallax";
import CardRipple from "./CardRipple";
import InfiniteCarousel from "./InfiniteCarousel";
import { scrollToSection } from "../lib/scroll";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";


function goInquire() {
  scrollToSection("contact");
}

const kanjiByTag = {
  Metabolic: "代",
  "GH Secretagogues": "成",
  Recovery: "復",
  Cosmetic: "美",
  Nootropic: "知",
  Vitality: "活",
  Longevity: "寿",
  Immune: "免",
  Blends: "合",
};

function DSection({ title, children }) {
  if (!children) return null;
  return (
    <div className="mt-7">
      <h4 className="font-mono2 mb-2 text-[10px] uppercase tracking-[0.3em] text-[#d91616]">{title}</h4>
      <p className="font-body text-sm leading-relaxed text-white/65">{children}</p>
    </div>
  );
}

function DList({ title, items, amber }) {
  if (!items || !items.length) return null;
  return (
    <div className="mt-7">
      <h4 className="font-mono2 mb-3 text-[10px] uppercase tracking-[0.3em] text-[#d91616]">{title}</h4>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex gap-3 font-body text-sm leading-relaxed text-white/65">
            <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 ${amber ? "bg-amber-400" : "bg-[#d91616]"}`} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProductCard({ p, index, reveal = true }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const kanji = kanjiByTag[p.tag] || "峠";

  const handleOpenChange = (v) => {
    setOpen(v);
  };

  return (
    <motion.article
      data-testid={`product-${p.id}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...(reveal
        ? {
            initial: { opacity: 0, y: 40 },
            whileInView: { opacity: 1, y: 0 },
            viewport: { once: true, margin: "-60px" },
            transition: { duration: 0.6, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] },
          }
        : { initial: false })}
      className="tracing-card group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#1c1c1c] bg-[#0c0c0c] p-5 transition-[transform,box-shadow] duration-500 hover:-translate-y-2 hover:shadow-[0_0_60px_-15px_rgba(217,22,22,0.55)]"
    >
      {hover && <CardRipple />}
      <span className="pulse-bloom z-20" aria-hidden="true" />
      <span className="slice-line z-30" aria-hidden="true" />
      <span className="card-kanji font-jp">{kanji}</span>
      <span className="reticle-corner tl" aria-hidden="true" />
      <span className="reticle-corner tr" aria-hidden="true" />
      <span className="reticle-corner bl" aria-hidden="true" />
      <span className="reticle-corner br" aria-hidden="true" />

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <button
            data-testid={`product-dossier-${p.id}`}
            className="slash-host absolute left-3 top-3 z-40 flex items-center gap-1.5 rounded-full border border-[#d91616]/40 bg-black/70 px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.22em] text-white/75 backdrop-blur-sm transition-all duration-300 hover:border-[#d91616] hover:text-white hover:shadow-[0_0_20px_-4px_rgba(217,22,22,0.7)]"
          >
            <FileText size={12} className="text-[#d91616]" /> Dossier
            <Slash />
          </button>
        </DialogTrigger>
        <DialogContent
          data-testid={`dossier-content-${p.id}`}
          className="max-h-[88vh] w-[92vw] max-w-lg overflow-hidden rounded-2xl border-[#d91616]/25 bg-[#0a0a0a] p-0 text-white shadow-[0_0_90px_-12px_rgba(217,22,22,0.55)] [&>button]:text-white/60 [&>button:hover]:text-[#d91616]"
        >
          <span className="pointer-events-none absolute inset-x-0 top-0 z-40 h-[2px] bg-gradient-to-r from-transparent via-[#d91616] to-transparent" />
          <span className="pointer-events-none absolute right-3 top-24 z-0 font-jp text-[10rem] leading-none text-white/[0.035]">
            {kanji}
          </span>
          <DialogTitle className="sr-only">{p.name} dossier</DialogTitle>
          <DialogDescription className="sr-only">
            Research dossier and dosage guide for {p.name}.
          </DialogDescription>
          <motion.div
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.55, 0] }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="pointer-events-none absolute inset-0 z-[55] bg-[radial-gradient(circle,rgba(255,255,255,0.55)_0%,rgba(255,60,60,0.35)_35%,rgba(217,22,22,0)_70%)]"
          />
          <div className="pointer-events-none absolute inset-0 z-[60] flex items-center justify-center overflow-hidden">
            <motion.div
              aria-hidden="true"
              initial={{ x: "-140%", opacity: 0 }}
              animate={{ x: "140%", opacity: [0, 1, 1, 0] }}
              transition={{ duration: 0.55, ease: [0.5, 0, 0.2, 1] }}
              style={{ rotate: -20 }}
              className="relative h-[3px] w-[200%] bg-gradient-to-r from-transparent via-white to-[#ff2d2d]"
            >
              <span className="absolute -top-2 left-1/2 h-5 w-5 -translate-x-1/2 rounded-full bg-white shadow-[0_0_30px_14px_rgba(255,60,60,0.9)]" />
            </motion.div>
          </div>
          <div
            data-lenis-prevent
            className="max-h-[88vh] overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] [touch-action:pan-y]"
          >
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-center gap-3 py-2">
                <img
                  src={`${process.env.PUBLIC_URL || ""}/assets/mask-logo.png`}
                  alt="Toji Peptides"
                  className="h-11 w-11 object-contain brightness-0 invert"
                />
                <span className="font-display text-2xl font-black uppercase tracking-[0.18em] text-white">
                  TOJI<span className="text-[#d91616]">PEP</span>
                </span>
              </div>

              <div className="mt-6 flex items-center gap-2">
                <span className="h-1.5 w-1.5 bg-[#d91616]" />
                <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#d91616]">
                  {p.tag}
                </span>
              </div>
              <h3 className="font-display mt-2 text-3xl font-black tracking-tight md:text-4xl">
                {p.name}
              </h3>
              <p className="font-mono2 mt-1 text-[10px] uppercase tracking-[0.25em] text-white/40">
                {p.mg} mg vial{p.note ? ` ${p.note}` : ""}{p.route ? ` · ${p.route}` : ""}
              </p>

              <p className="mt-5 border-l-2 border-[#d91616] pl-4 font-body text-sm leading-relaxed text-white/70">
                {p.overview || p.blurb}
              </p>

              <div className="mt-6 grid grid-cols-2 gap-2">
                {[
                  ["Dose", p.dose],
                  ["Frequency", p.frequency],
                  ["Half-life", p.half_life],
                  ["Cycle", p.cycle],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-[#1c1c1c] bg-[#0d0d0d] p-3">
                    <div className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-white/35">{k}</div>
                    <div className="mt-1 font-body text-[13px] leading-snug text-white/85">{v || "—"}</div>
                  </div>
                ))}
              </div>

              <DSection title="Mechanism of Action">{p.mechanism}</DSection>
              <DSection title="Research Notes">{p.research}</DSection>
              <DList title="Benefits" items={p.benefits} />
              <DList title="Administration & Reconstitution" items={p.administration} />
              <DSection title="Storage">{p.storage}</DSection>
              <DList title="Stack Synergies" items={p.stacks} />
              <DList title="Cautions" items={p.cautions} amber />

              <p className="font-mono2 mt-7 text-[9px] leading-relaxed tracking-wide text-white/30">
                Informational and research use only. Not for human consumption and
                not medical advice. Dosage figures reflect commonly reported
                research ranges.
              </p>

              <button
                data-testid={`dossier-inquire-${p.id}`}
                onClick={() => {
                  setOpen(false);
                  goInquire();
                }}
                className="slash-host mt-6 flex w-full items-center justify-center gap-2 bg-[#d91616] py-4 font-mono2 text-xs uppercase tracking-[0.3em] text-white transition-colors hover:bg-[#7a0000]"
              >
                Request Access <ArrowUpRight size={14} />
                <Slash />
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="relative z-10 flex flex-1 flex-col justify-center py-10">
        <span className="font-mono2 text-[10px] uppercase tracking-[0.35em] text-white/35">
          {p.mg} MG {p.note ? p.note : ""}
        </span>
        <h3 className="font-display mt-3 text-4xl font-black leading-[0.9] tracking-tighter text-white transition-colors duration-500 group-hover:text-[#d91616] md:text-5xl">
          {p.name}
        </h3>
        <span className="font-mono2 mt-3 block text-[10px] uppercase tracking-[0.3em] text-white/25">
          {p.batch}
        </span>
      </div>

      <div className="relative z-10 mt-5 flex items-center justify-between border-t border-[#1c1c1c] pt-4">
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 bg-[#d91616]" />
          <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/50">
            {p.tag}
          </span>
        </div>
        <button
          data-testid={`product-cta-${p.id}`}
          onClick={() => goInquire()}
          className="slash-host flex items-center gap-1 px-1 py-1 font-mono2 text-xs uppercase tracking-[0.2em] text-white/70 transition-colors group-hover:text-[#d91616]"
        >
          Request Access <ArrowUpRight size={14} />
          <Slash />
        </button>
      </div>
    </motion.article>
  );
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState("ALL");
  const [query, setQuery] = useState("");

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/peptides`)
      .then((r) => r.json())
      .then((data) => setProducts(data.map((p) => ({ ...p, id: p.slug }))))
      .catch(() => setProducts([]));
  }, []);

  const CATEGORIES = [
    "ALL",
    ...products.reduce((acc, p) => (acc.includes(p.tag) ? acc : [...acc, p.tag]), []),
  ];
  const byTab = activeTab === "ALL" ? products : products.filter((p) => p.tag === activeTab);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? byTab.filter(
        (p) =>
          p.name.toLowerCase().includes(q) || (p.tag || "").toLowerCase().includes(q)
      )
    : byTab;

  return (
    <section
      id="products"
      data-testid="products"
      className="relative overflow-hidden bg-[#050505] py-28 md:py-40"
    >
      <ParallaxShader variant="ink" intensity={0.8} strength={28} />
      <ParallaxSpan
        className="ambient-red"
        style={{ width: "42rem", height: "42rem", top: "-10rem", right: "-12rem" }}
        strength={50}
      />
      <ParallaxSpan
        className="ink-blot"
        style={{ width: "30rem", height: "30rem", bottom: "-6rem", left: "-8rem" }}
        strength={65}
      />
      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="relative mb-8 md:pl-4">
          <ParallaxSpan className="section-kanji font-jp -top-20 left-0 text-[10rem] md:text-[16rem]" strength={95}>
            武
          </ParallaxSpan>
          <ScrambleText
            as="span"
            text="02 — THE ARSENAL"
            className="font-mono2 relative mb-4 block text-xs uppercase tracking-[0.4em] text-[#d91616]"
          />
          <PointerLayer strength={-16}>
            <h2 className="text-glow-hover font-display relative text-5xl font-black leading-[0.9] tracking-tighter md:text-7xl">
              WEAPONS FOR
              <br />
              <span className="text-stroke-red">THE BODY.</span>
            </h2>
          </PointerLayer>
          <p className="mt-5 max-w-xl font-body text-sm leading-relaxed text-white/45">
            An educational dossier for every compound in the arsenal — mechanism,
            research-backed dosing, protocols, and cautions. Informational use only.
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
          <input
            data-testid="arsenal-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the database…"
            className="w-full rounded-lg border border-[#1c1c1c] bg-[#0c0c0c] py-2.5 pl-9 pr-9 font-body text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-[#d91616]/60"
          />
          {query && (
            <button
              data-testid="arsenal-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 font-mono2 text-[10px] uppercase tracking-widest text-white/40 hover:text-white"
            >
              Clear
            </button>
          )}
        </div>

        {/* Category filter tabs */}
        <div
          data-testid="arsenal-tabs"
          role="tablist"
          className="relative flex flex-nowrap gap-x-7 gap-y-3 overflow-x-auto border-b border-[#1c1c1c] pb-4 [scrollbar-width:none] md:flex-wrap md:overflow-visible [&::-webkit-scrollbar]:hidden"
        >
          {CATEGORIES.map((cat) => {
            const active = activeTab === cat;
            const count =
              cat === "ALL" ? products.length : products.filter((p) => p.tag === cat).length;
            return (
              <button
                key={cat}
                role="tab"
                aria-selected={active}
                data-testid={`arsenal-tab-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActiveTab(cat)}
                className="relative shrink-0 pb-2 font-mono2 text-[11px] uppercase tracking-[0.25em] transition-colors"
              >
                <span className={active ? "text-white" : "text-white/40 hover:text-white/70"}>
                  {cat}
                  <sup className="ml-1 text-[8px] text-[#d91616]">{count}</sup>
                </span>
                {active && (
                  <motion.span
                    layoutId="arsenal-tab-underline"
                    className="absolute -bottom-[17px] left-0 right-0 h-[2px] bg-[#d91616] shadow-[0_0_12px_rgba(217,22,22,0.9)]"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Infinite horizontal carousel */}
      <div className="relative z-10 mt-14">
        {filtered.length === 0 ? (
          <p
            data-testid="arsenal-empty"
            className="py-16 text-center font-mono2 text-xs uppercase tracking-[0.3em] text-white/40"
          >
            {products.length === 0 ? "Loading arsenal…" : "No compounds match your search."}
          </p>
        ) : (
          <>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab + "|" + (q ? "q" : "")}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              >
                <InfiniteCarousel
                  items={filtered}
                  renderItem={(p, i) => (
                    <TiltCard className="h-full">
                      <ProductCard p={p} index={i} reveal={false} />
                    </TiltCard>
                  )}
                />
              </motion.div>
            </AnimatePresence>
            <p className="font-mono2 mt-6 text-center text-[10px] uppercase tracking-[0.3em] text-white/30">
              ← Drag or scroll horizontally to explore →
            </p>
          </>
        )}
      </div>
    </section>
  );
}
